import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Box,
  Stack,
  Typography,
  Rating,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { AddtaskRating, getTaskRating, clearTaskRating } from '../../../redux/features/taskManagementSlice';

// rating_type mapping (as defined by the API contract)
export const RATING_TYPES = {
  RECEIVER: 1, // the user who created (received the delivery of) the task
  HEAD: 2, // reporting head of the assigned employee
};

const RATING_TYPE_LABEL = {
  1: 'Task Creator',
  2: 'Reporting Head',
};

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('cred'))?.profile || {};
  } catch {
    return {};
  }
};

// Decide which rating_type the current user is allowed to give.
// Creator -> RECEIVER(1); person who assigned (head) -> HEAD(2).
export const resolveRatingType = (currentUserName, createdByName, assignedByName) => {
  if (currentUserName && createdByName && currentUserName === createdByName) return RATING_TYPES.RECEIVER;
  if (currentUserName && assignedByName && currentUserName === assignedByName) return RATING_TYPES.HEAD;
  return null;
};

const normalizeRating = (r = {}) => ({
  ratingType: Number(r.rating_type ?? r.type),
  typeLabel: r.rating_type_label ?? '',
  rating: Number(r.rating ?? r.stars ?? 0),
  review: r.review ?? r.remarks ?? r.comment ?? '',
  by: r.created_by_name ?? r.rated_by_name ?? r.user_name ?? r.name ?? '',
  ratedForName: r.rated_for_name ?? '',
});

const TaskRating = ({ taskId, createdByName, assignedByName }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useMemo(getCurrentUser, []);

  const { taskRating } = useSelector((state) => state.taskManagement);

  const ratingType = resolveRatingType(currentUser?.name, createdByName, assignedByName);

  const [fetching, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stars, setStars] = useState(0);
  const [review, setReview] = useState('');
  // taskId whose ratings are currently in the store; hides the previous task's
  // ratings/prefill until the new fetch resolves.
  const [loadedFor, setLoadedFor] = useState(null);

  const ratings = useMemo(
    () => (Array.isArray(taskRating) ? taskRating.map(normalizeRating) : []),
    [taskRating]
  );

  const myExisting = useMemo(
    () => ratings.find((r) => r.ratingType === ratingType),
    [ratings, ratingType]
  );

  const load = () => {
    if (!taskId) return;
    setFetching(true);
    dispatch(getTaskRating(taskId)).finally(() => {
      setFetching(false);
      setLoadedFor(taskId);
    });
  };

  const ready = loadedFor === taskId;

  useEffect(() => {
    // Clear the form and previous task's ratings when switching tasks.
    setStars(0);
    setReview('');
    dispatch(clearTaskRating());
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  // Prefill only the star value from the current user's existing rating (upsert
  // behaviour). The review field is intentionally left empty so the previously
  // submitted review text is never pre-populated.
  useEffect(() => {
    if (ready && myExisting) {
      setStars(myExisting.rating || 0);
    }
  }, [ready, myExisting]);

  const handleSubmit = () => {
    if (!ratingType) return;
    if (!stars) {
      enqueueSnackbar('Please select a star rating', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    dispatch(
      AddtaskRating({ task_id: taskId, rating_type: ratingType, rating: stars, review: review.trim() })
    ).then((res) => {
      setSubmitting(false);
      if (res?.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message || 'Rating submitted', { variant: 'success' });
        load();
      } else {
        enqueueSnackbar(res?.payload?.message || 'Failed to submit rating', { variant: 'error' });
      }
    });
  };

  return (
    <Box>
      {/* Existing ratings summary */}
      {!ready ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : ratings.length > 0 ? (
        <Stack spacing={1.25} sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Ratings given
          </Typography>
          {ratings.map((r, i) => (
            <Box
              key={i}
              sx={{
                border: '1px solid #EEF2F6',
                borderRadius: 2.5,
                px: 1.75,
                py: 1.25,
                background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Chip
                  label={r.typeLabel || RATING_TYPE_LABEL[r.ratingType] || 'Rating'}
                  size="small"
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#EEF2FF', color: '#4338CA' }}
                />
                <Rating
                  value={r.rating}
                  readOnly
                  size="small"
                  icon={<StarRoundedIcon fontSize="inherit" />}
                  emptyIcon={<StarBorderRoundedIcon fontSize="inherit" />}
                  sx={{ color: '#F59E0B' }}
                />
              </Stack>
              {r.review && (
                <Typography sx={{ mt: 0.75, fontSize: '0.82rem', color: '#475569', lineHeight: 1.55 }}>
                  “{r.review}”
                </Typography>
              )}
              {(r.by || r.ratedForName) && (
                <Typography sx={{ mt: 0.5, fontSize: '0.7rem', color: '#94A3B8' }}>
                  {r.by ? `By ${r.by}` : ''}
                  {r.ratedForName ? ` · For ${r.ratedForName}` : ''}
                </Typography>
              )}
            </Box>
          ))}
          <Divider sx={{ my: 0.5, borderColor: '#F1F5F9' }} />
        </Stack>
      ) : null}

      {/* Submit / update form (only if current user can rate) */}
      {ratingType ? (
        <Box
          sx={{
            border: '1px solid #E2E8F0',
            borderRadius: 3,
            p: 2.25,
            background: '#fff',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #F59E0B, #F97316)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RateReviewOutlinedIcon sx={{ fontSize: 17, color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#1E293B' }}>
                {myExisting ? 'Update your rating' : 'Rate this task'}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>
                Rating as {RATING_TYPE_LABEL[ratingType]}
              </Typography>
            </Box>
          </Stack>

          <Stack alignItems="center" sx={{ py: 1 }}>
            <Rating
              value={stars}
              onChange={(e, v) => setStars(v || 0)}
              size="large"
              icon={<StarRoundedIcon fontSize="inherit" />}
              emptyIcon={<StarBorderRoundedIcon fontSize="inherit" />}
              sx={{ color: '#F59E0B', fontSize: '2.4rem' }}
            />
            <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', mt: 0.25 }}>
              {stars ? `${stars} / 5` : 'Tap a star to rate'}
            </Typography>
          </Stack>

          <TextField
            label="Review (optional)"
            value={review}
            onChange={(e) => setReview(e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={15} color="inherit" /> : <StarRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                background: 'linear-gradient(135deg, #F59E0B, #F97316)',
                '&:hover': { background: 'linear-gradient(135deg, #D97706, #EA580C)' },
              }}
            >
              {submitting ? 'Saving...' : myExisting ? 'Update Rating' : 'Submit Rating'}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, color: '#94A3B8' }}>
          <StarBorderRoundedIcon sx={{ fontSize: 40, color: '#CBD5E1' }} />
          <Typography sx={{ fontSize: '0.85rem' }}>You don’t have permission to rate this task.</Typography>
        </Stack>
      )}
    </Box>
  );
};

export default TaskRating;
